import {
	IGetRestaurantsShortArgs,
	ICreateRestaurant,
	IUpdateRestaurant,
	IGetPaginatedRestaurants,
	IGetRestaurantLocalizationInfoArgs,
	ICreateRestaurantLocalization,
	IUpdateRestaurantLocalization,
	IUpdateRestaurantStyles,
	ICreateRestaurantWatchesCommandsFile,
	IPushNotification,
	IGetPaginatedRestaurantSoftwareUpdates,
	IGetPaginatedRestaurantDevices,
	IAssignDevices,
	IRestaurantsCopyMenu,
	ICreateRestaurantReports,
	IUpdateRestaurantReports,
	IUpdateRestaurantBankSettings,
	IUpdateRestaurantNfcCards,
} from 'types';
import {
	serviceGetRestaurantInfo,
	serviceGetRestaurantsShort,
	serviceGetRestaurantsByCategoryShort,
	serviceGetRestaurantsByDishShort,
	serviceCreateRestaurant,
	serviceUpdateRestaurant,
	serviceGetPaginatedRestaurants,
	serviceDeleteRestaurant,
	serviceArchiveRestaurant,
	serviceRestoreRestaurant,
	serviceGetRestaurantLocalizationInfo,
	serviceCreateRestaurantLocalization,
	serviceUpdateRestaurantLocalization,
	serviceGetRestaurantStylesInfo,
	serviceUpdateRestaurantStylesInfo,
	serviceGetRestaurantDishes,
	serviceCreateRestaurantWatchesCommandsFile,
	servicePushRestaurantNotification,
	serviceGetLatestRestaurantSoftwareUpdatesData,
	serviceGetPaginatedRestaurantSoftwareUpdates,
	serviceGetPaginatedRestaurantDevices,
	serviceAssignDevices,
	serviceCopyMenu,
	serviceCopyMenuV2,
	serviceGetReportsInfo,
	serviceGetRestaurantReportsInfo,
	serviceCreateRestaurantReports,
	serviceUpdateRestaurantReports,
	serviceDeleteRestaurantReports,
	serviceUploadRestaurantBankSettings,
	serviceGetRestaurantBankSettings,
	serviceUpdateRestaurantBankSettings,
	serviceGetRestaurantTlsCertificateInfo,
	serviceGetRestaurantNfcCard,
	serviceUpdateRestaurantNfcCard,
} from 'services/api/restaurants';
import { createAppErrorMessage } from 'utils/helpers';

const getRestaurantInfo = async (restaurantID: string) => {
	try {
		const response = await serviceGetRestaurantInfo(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsShortInfo = async (args: IGetRestaurantsShortArgs) => {
	try {
		const response = await serviceGetRestaurantsShort(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsByCategoryShortInfo = async (categoryId: string) => {
	try {
		const response = await serviceGetRestaurantsByCategoryShort(categoryId);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsByDishShortInfo = async (dishId: string) => {
	try {
		const response = await serviceGetRestaurantsByDishShort(dishId);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createRestaurant = async (data: ICreateRestaurant) => {
	try {
		await serviceCreateRestaurant(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingRestaurant');
		throw new Error(errorMessage);
	}
};

const updateRestaurant = async (data: IUpdateRestaurant) => {
	try {
		await serviceUpdateRestaurant(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingRestaurant');
		throw new Error(errorMessage);
	}
};

const getPaginatedRestaurants = async (args: IGetPaginatedRestaurants) => {
	try {
		const response = await serviceGetPaginatedRestaurants(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteRestaurant = async (restaurantID: string) => {
	try {
		await serviceDeleteRestaurant(restaurantID);
	} catch (err) {
		throw err;
	}
};

const archiveRestaurant = async (restaurantID: string) => {
	try {
		await serviceArchiveRestaurant(restaurantID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorArchivingRestaurant');
		throw new Error(errorMessage);
	}
};

const restoreRestaurant = async (restaurantID: string) => {
	try {
		await serviceRestoreRestaurant(restaurantID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorRestoringRestaurant');
		throw new Error(errorMessage);
	}
};

const getRestaurantLocalizationInfo = async (args: IGetRestaurantLocalizationInfoArgs) => {
	try {
		const response = await serviceGetRestaurantLocalizationInfo(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const createRestaurantLocalization = async (data: ICreateRestaurantLocalization) => {
	try {
		await serviceCreateRestaurantLocalization(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingRestaurantLocalization');
		throw new Error(errorMessage);
	}
};

const updateRestaurantLocalization = async (data: IUpdateRestaurantLocalization) => {
	try {
		await serviceUpdateRestaurantLocalization(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingRestaurantLocalization');
		throw new Error(errorMessage);
	}
};

const getRestaurantStylesInfo = async (customerID: string, restaurantID: string) => {
	try {
		const response = await serviceGetRestaurantStylesInfo(customerID, restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const updateRestaurantStyles = async (customerID: string, restaurantID: string, data: IUpdateRestaurantStyles) => {
	try {
		await serviceUpdateRestaurantStylesInfo(customerID, restaurantID, data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingRestaurantStyles');
		throw new Error(errorMessage);
	}
};

const getRestaurantDishes = async (restaurantID: string) => {
	try {
		const response = await serviceGetRestaurantDishes(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const createRestaurantWatchesCommandsFile = async (
	restaurantID: string,
	data: ICreateRestaurantWatchesCommandsFile,
) => {
	try {
		await serviceCreateRestaurantWatchesCommandsFile(restaurantID, data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingRestaurantWatches');
		throw new Error(errorMessage);
	}
};

const pushRestaurantNotification = async (restaurantID: string, data: IPushNotification) => {
	try {
		await servicePushRestaurantNotification(restaurantID, data);
	} catch (err) {
		throw err;
	}
};

const getLatestRestaurantSoftwareUpdatesData = async (restaurantID: string) => {
	try {
		const response = await serviceGetLatestRestaurantSoftwareUpdatesData(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getPaginatedRestaurantSoftwareUpdates = async (args: IGetPaginatedRestaurantSoftwareUpdates) => {
	try {
		const response = await serviceGetPaginatedRestaurantSoftwareUpdates(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getPaginatedRestaurantDevices = async (args: IGetPaginatedRestaurantDevices) => {
	try {
		const response = await serviceGetPaginatedRestaurantDevices(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const assignDevices = async (data: IAssignDevices) => {
	try {
		await serviceAssignDevices(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorAssignDevicesToRestaurant');
		throw new Error(errorMessage);
	}
};

// Menu v1
const copyMenu = async (data: IRestaurantsCopyMenu) => {
	try {
		await serviceCopyMenu(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCopyMenuToRestaurants');
		throw new Error(errorMessage);
	}
};
// Menu v1
// Menu v2
const copyMenuV2 = async (data: IRestaurantsCopyMenu) => {
	try {
		await serviceCopyMenuV2(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCopyMenuToRestaurants');
		throw new Error(errorMessage);
	}
};
// Menu v2

const getReportsInfo = async (reportsID: string) => {
	try {
		const response = await serviceGetReportsInfo(reportsID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantReportsInfo = async (restaurantReportsID: string) => {
	try {
		const response = await serviceGetRestaurantReportsInfo(restaurantReportsID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const createRestaurantReports = async (data: ICreateRestaurantReports) => {
	try {
		await serviceCreateRestaurantReports(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingRestaurantReports');
		throw new Error(errorMessage);
	}
};

const updateRestaurantReports = async (data: IUpdateRestaurantReports) => {
	try {
		await serviceUpdateRestaurantReports(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingRestaurantReports');
		throw new Error(errorMessage);
	}
};

const deleteRestaurantReports = async (reportID: string) => {
	try {
		await serviceDeleteRestaurantReports(reportID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingRestaurantReport');
		throw new Error(errorMessage);
	}
};

const uploadRestaurantBankSettings = async (data: FormData) => {
	try {
		const response = await serviceUploadRestaurantBankSettings(data);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorAddingRestaurantBankSettingsFile');
		throw new Error(errorMessage);
	}
};

const getRestaurantBankSettings = async (restaurantID: string) => {
	try {
		const response = await serviceGetRestaurantBankSettings(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const updateRestaurantBankSettings = async (data: IUpdateRestaurantBankSettings) => {
	try {
		await serviceUpdateRestaurantBankSettings(data);
	} catch (err) {
		throw err;
	}
};

const getRestaurantTlsCertificateInfo = async (restaurantID: string) => {
	try {
		const response = await serviceGetRestaurantTlsCertificateInfo(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantNfcCard = async (restaurantID: string) => {
	try {
		const response = await serviceGetRestaurantNfcCard(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const updateRestaurantNfcCard = async (data: IUpdateRestaurantNfcCards) => {
	try {
		await serviceUpdateRestaurantNfcCard(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingRestaurantNfcCard');
		throw new Error(errorMessage);
	}
};

export {
	getRestaurantInfo,
	getRestaurantsShortInfo,
	getRestaurantsByCategoryShortInfo,
	getRestaurantsByDishShortInfo,
	createRestaurant,
	updateRestaurant,
	getPaginatedRestaurants,
	deleteRestaurant,
	archiveRestaurant,
	restoreRestaurant,
	getRestaurantLocalizationInfo,
	createRestaurantLocalization,
	updateRestaurantLocalization,
	getRestaurantStylesInfo,
	updateRestaurantStyles,
	getRestaurantDishes,
	createRestaurantWatchesCommandsFile,
	pushRestaurantNotification,
	getLatestRestaurantSoftwareUpdatesData,
	getPaginatedRestaurantSoftwareUpdates,
	getPaginatedRestaurantDevices,
	assignDevices,
	copyMenu,
	copyMenuV2,
	getReportsInfo,
	getRestaurantReportsInfo,
	createRestaurantReports,
	updateRestaurantReports,
	deleteRestaurantReports,
	uploadRestaurantBankSettings,
	getRestaurantBankSettings,
	updateRestaurantBankSettings,
	getRestaurantTlsCertificateInfo,
	getRestaurantNfcCard,
	updateRestaurantNfcCard,
};
